import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getRecipe, recipes } from '../data/recipes';
import { getProduct } from '../data/products';
import { useSavedRituals } from '../context/SavedRitualsContext';
import { ChevronLeft, Clock, BookOpen, ArrowRight, Bookmark, Bell, Check, Share2, NotebookPen, Save as SaveIcon, Flame, Printer, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const RecipeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const recipe = getRecipe(id);
  const {
    isSaved, toggleSaved, reminders, setReminder, removeReminder,
    notificationPermission, requestNotificationPermission,
    getNote, setNote,
    isDoneToday, markDone, unmarkDone, getStreak, getTotalCompletions,
  } = useSavedRituals();
  const [reminderTime, setReminderTime] = useState('');
  const [noteDraft, setNoteDraft] = useState('');
  const [noteDirty, setNoteDirty] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, [id]);
  useEffect(() => {
    if (recipe) {
      setReminderTime(reminders[recipe.id]?.time || '');
      setNoteDraft(getNote(recipe.id));
      setNoteDirty(false);
    }
  }, [recipe, reminders, getNote]);

  if (!recipe) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-24 text-center">
        <h2 className="font-serif-elegant text-3xl mb-4">Ritual not found</h2>
        <Link to="/recipes" className="btn-primary">Back to Recipe Journal</Link>
      </div>
    );
  }

  const related = recipe.relatedProducts?.map(getProduct).filter(Boolean) || [];
  const nextRecipe = recipes[(recipes.findIndex(r => r.id === recipe.id) + 1) % recipes.length];
  const saved = isSaved(recipe.id);
  const activeReminder = reminders[recipe.id];

  const onSaveClick = () => {
    toggleSaved(recipe.id);
    toast.success(saved ? 'Removed from your rituals' : 'Saved to your rituals');
  };

  const onShare = async () => {
    const url = window.location.href;
    const shareData = {
      title: `${recipe.title} \u2014 Herbs We Love`,
      text: `${recipe.title}: ${recipe.subtitle}`,
      url,
    };
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      try {
        await navigator.share(shareData);
        return;
      } catch (e) {
        // User cancelled share, fall back to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link copied. Share it with someone you love.');
    } catch (e) {
      toast.error('Could not copy link. Please copy from the address bar.');
    }
  };

  const saveNote = () => {
    setNote(recipe.id, noteDraft);
    setNoteDirty(false);
    toast.success(noteDraft.trim() ? 'Note saved' : 'Note cleared');
  };

  const doneToday = recipe ? isDoneToday(recipe.id) : false;
  const streak = recipe ? getStreak(recipe.id) : 0;
  const totalDone = recipe ? getTotalCompletions(recipe.id) : 0;

  const onMarkDone = () => {
    if (!recipe) return;
    if (doneToday) {
      unmarkDone(recipe.id);
      toast.success('Marked as not done today');
    } else {
      markDone(recipe.id, recipe.title);
      const newStreak = getStreak(recipe.id) + 1;
      toast.success(newStreak > 1 ? `Beautiful. ${newStreak}-day streak \u{1F525}` : 'Beautiful. Your streak starts today.');
    }
  };

  const openPrint = () => {
    window.open(`/recipes/${recipe.id}/print`, '_blank');
  };

  const onTimeChange = async (e) => {
    const value = e.target.value;
    setReminderTime(value);
    if (value) {
      setReminder(recipe.id, value, recipe.title);
      if (notificationPermission !== 'granted') {
        const result = await requestNotificationPermission();
        if (result === 'granted') {
          toast.success('Reminder set. We\u2019ll gently nudge you at ' + value);
        } else {
          toast.success('Reminder saved. Notifications are off, but you\u2019ll still see an in-app banner.');
        }
      } else {
        toast.success('Reminder set for ' + value);
      }
    } else {
      removeReminder(recipe.id);
      toast.success('Reminder removed');
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative">
        <div className="aspect-[16/7] md:aspect-[16/6] overflow-hidden bg-[#efe9db]">
          <img src={recipe.image} alt={recipe.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/50" />
        </div>
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-4xl mx-auto w-full px-6 lg:px-10 pb-8 md:pb-12 text-[#faf7f1]">
            <button onClick={() => navigate('/recipes')} className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#dcd6c7] hover:text-white mb-4">
              <ChevronLeft className="w-4 h-4" /> Back to journal
            </button>
            <div className="inline-flex items-center gap-2 uppercase tracking-[0.35em] text-xs text-[#dcd6c7] mb-3">
              <BookOpen className="w-4 h-4" /> {recipe.moment}
            </div>
            <h1 className="font-serif-elegant text-4xl md:text-6xl mb-2">{recipe.title}</h1>
            <p className="font-script text-xl md:text-2xl text-[#dcd6c7]">{recipe.subtitle}</p>
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12 md:py-16">
        <div className="flex flex-wrap items-center gap-4 text-xs uppercase tracking-widest text-[#7a8b6b] pb-6 border-b border-[#e5decf] mb-8">
          <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {recipe.time}</span>
          <span>&middot;</span>
          <span>{recipe.difficulty}</span>
          <span>&middot;</span>
          <span>{recipe.moment}</span>
        </div>

        <p className="text-lg text-[#4a4640] leading-relaxed mb-8">{recipe.intro}</p>

        {/* Save + share + reminder controls */}
        <div className="mb-6 bg-[#efe9db] p-5 md:p-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={onSaveClick}
              className={`inline-flex items-center gap-2 px-4 py-2 border transition-colors ${saved ? 'bg-[#3a4a3b] border-[#3a4a3b] text-[#faf7f1]' : 'border-[#3a4a3b] text-[#3a4a3b] hover:bg-[#3a4a3b] hover:text-[#faf7f1]'}`}
            >
              {saved ? <><Check className="w-4 h-4" /> Saved</> : <><Bookmark className="w-4 h-4" /> Save ritual</>}
            </button>
            <button
              onClick={onShare}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#3a4a3b] text-[#3a4a3b] hover:bg-[#3a4a3b] hover:text-[#faf7f1] transition-colors"
            >
              <Share2 className="w-4 h-4" /> Share
            </button>
            <button
              onClick={openPrint}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#3a4a3b] text-[#3a4a3b] hover:bg-[#3a4a3b] hover:text-[#faf7f1] transition-colors"
            >
              <Printer className="w-4 h-4" /> Print card
            </button>
          </div>
          <div className="flex items-center gap-3">
            <Bell className={`w-5 h-5 ${activeReminder ? 'text-[#3a4a3b]' : 'text-[#6f6a5f]'}`} />
            <div className="text-sm">
              <div className="text-xs uppercase tracking-widest text-[#6f6a5f] mb-1">Daily reminder</div>
              <input
                type="time"
                value={reminderTime}
                onChange={onTimeChange}
                className="bg-transparent border border-[#c9c1ad] px-2 py-1 text-sm focus:outline-none focus:border-[#3a4a3b]"
              />
            </div>
          </div>
        </div>

        {/* Mark done + streak */}
        <div className="mb-8 bg-[#faf7f1] border border-[#e5decf] p-5 md:p-6 flex flex-wrap items-center gap-4 justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={onMarkDone}
              className={`inline-flex items-center gap-2 px-5 py-2.5 border transition-colors ${doneToday ? 'bg-[#7a8b6b] border-[#7a8b6b] text-[#faf7f1]' : 'border-[#7a8b6b] text-[#7a8b6b] hover:bg-[#7a8b6b] hover:text-[#faf7f1]'}`}
            >
              <CheckCircle2 className={`w-5 h-5 ${doneToday ? 'fill-current text-[#faf7f1]' : ''}`} />
              {doneToday ? 'Done today' : 'Mark as done today'}
            </button>
            {totalDone > 0 && (
              <div className="text-xs text-[#6f6a5f]">
                {totalDone} time{totalDone > 1 ? 's' : ''} total
              </div>
            )}
          </div>
          {streak > 0 && (
            <div className="inline-flex items-center gap-2 bg-[#7a3b2e]/10 text-[#7a3b2e] px-4 py-2">
              <Flame className="w-5 h-5 fill-current" />
              <div>
                <div className="font-serif-elegant text-2xl leading-none">{streak}</div>
                <div className="text-[10px] uppercase tracking-widest">day{streak > 1 ? 's' : ''} in a row</div>
              </div>
            </div>
          )}
        </div>

        {/* Personal note */}
        <div className="mb-12 bg-[#faf7f1] border border-[#e5decf] p-5 md:p-6">
          <div className="flex items-center gap-2 mb-3">
            <NotebookPen className="w-4 h-4 text-[#3a4a3b]" />
            <div className="uppercase tracking-widest text-xs text-[#6f6a5f]">My ritual journal</div>
          </div>
          <textarea
            value={noteDraft}
            onChange={e => { setNoteDraft(e.target.value); setNoteDirty(true); }}
            rows={4}
            placeholder="How did this ritual feel? Anything you noticed, tweaked, or want to remember for next time..."
            className="w-full bg-transparent border border-[#c9c1ad] px-3 py-2 text-sm text-[#4a4640] leading-relaxed focus:outline-none focus:border-[#3a4a3b] resize-none"
          />
          <div className="flex flex-wrap items-center justify-between gap-3 mt-3">
            <div className="text-xs text-[#6f6a5f]">
              {noteDirty ? 'Unsaved changes' : (noteDraft ? 'Saved to your rituals' : 'Your note stays on this device')}
            </div>
            <button
              onClick={saveNote}
              disabled={!noteDirty}
              className="inline-flex items-center gap-2 px-4 py-2 border border-[#3a4a3b] text-[#3a4a3b] hover:bg-[#3a4a3b] hover:text-[#faf7f1] transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:text-[#3a4a3b] text-xs uppercase tracking-widest"
            >
              <SaveIcon className="w-3.5 h-3.5" /> Save note
            </button>
          </div>
        </div>

        <div className="grid md:grid-cols-[280px_1fr] gap-10 md:gap-16">
          {/* Ingredients */}
          <aside className="md:sticky md:top-28 md:self-start bg-[#efe9db] p-6">
            <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-4">You&rsquo;ll need</div>
            <ul className="space-y-3 text-sm">
              {recipe.ingredients.map((ing, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-[#7a8b6b] mt-0.5">&bull;</span>
                  {ing.productId ? (
                    <Link to={`/products/${ing.productId}`} className="text-[#3a4a3b] link-underline">{ing.text}</Link>
                  ) : (
                    <span className="text-[#4a4640]">{ing.text}</span>
                  )}
                </li>
              ))}
            </ul>
          </aside>

          {/* Steps */}
          <div>
            <div className="uppercase tracking-widest text-xs text-[#6f6a5f] mb-4">The ritual</div>
            <ol className="space-y-6">
              {recipe.steps.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <div className="font-serif-elegant text-3xl text-[#7a8b6b] leading-none w-10 shrink-0">{String(i + 1).padStart(2, '0')}</div>
                  <p className="text-[#4a4640] leading-relaxed pt-1">{step}</p>
                </li>
              ))}
            </ol>

            {recipe.tip && (
              <div className="mt-10 bg-[#3a4a3b]/5 border-l-4 border-[#3a4a3b] p-5">
                <div className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-2">A tender tip</div>
                <p className="text-[#4a4640] italic leading-relaxed">{recipe.tip}</p>
              </div>
            )}
          </div>
        </div>

        {/* Related products */}
        {related.length > 0 && (
          <section className="mt-20">
            <h2 className="font-serif-elegant text-3xl mb-6">The herbs for this ritual</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {related.map(p => (
                <Link key={p.id} to={`/products/${p.id}`} className="group flex gap-4 p-4 bg-[#faf7f1] border border-[#e5decf] hover:shadow-md transition-shadow">
                  <div className="w-24 h-24 bg-[#efe9db] shrink-0 overflow-hidden flex items-center justify-center p-2">
                    <img src={p.images[0]} alt={p.title} className="w-full h-full object-contain" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-serif-elegant text-lg leading-tight mb-1">{p.title}</div>
                    <div className="text-sm text-[#7a3b2e] font-medium mb-2">${p.price.toFixed(2)}</div>
                    <span className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#3a4a3b] group-hover:gap-2 transition-all">
                      Shop <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Next recipe */}
        <section className="mt-20 pt-10 border-t border-[#e5decf] flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="uppercase tracking-widest text-xs text-[#7a8b6b] mb-1">Next in the journal</div>
            <Link to={`/recipes/${nextRecipe.id}`} className="font-serif-elegant text-2xl link-underline">{nextRecipe.title}</Link>
          </div>
          <Link to="/recipes" className="btn-outline">All rituals</Link>
        </section>
      </div>
    </div>
  );
};

export default RecipeDetail;
